'use client';

import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

export type CallStatus = 'idle' | 'calling' | 'receiving' | 'active';
export type CallType = 'audio' | 'video';

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export function useCall(socketRef: MutableRefObject<Socket | null>, chatStatus: string) {
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [callType, setCallType] = useState<CallType>('video');
  const [incomingType, setIncomingType] = useState<CallType>('video');

  const peerRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const iceCandidateQueue = useRef<RTCIceCandidateInit[]>([]);
  const callTypeRef = useRef<CallType>('video');
  const incomingTypeRef = useRef<CallType>('video');
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);

  const cleanup = useCallback(() => {
    peerRef.current?.close();
    peerRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    iceCandidateQueue.current = [];
    setCallStatus('idle');
  }, []);

  const endCall = useCallback(() => {
    socketRef.current?.emit('call:end');
    cleanup();
  }, [socketRef, cleanup]);

  const createPeer = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socketRef.current?.emit('call:signal', { type: 'candidate', candidate: e.candidate });
      }
    };

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        cleanup();
      }
    };

    peerRef.current = pc;
    return pc;
  }, [socketRef, cleanup]);

  const getLocalMedia = useCallback(async (type: CallType) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: type === 'video',
    });
    localStreamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }
    return stream;
  }, []);

  const startCall = useCallback(
    async (type: CallType) => {
      if (!socketRef.current || callStatus !== 'idle') return;
      callTypeRef.current = type;
      setCallType(type);
      setCallStatus('calling');
      socketRef.current.emit('call:request', { type });
    },
    [socketRef, callStatus]
  );

  const acceptCall = useCallback(async () => {
    if (!socketRef.current) return;
    socketRef.current.emit('call:accept');
    setCallStatus('active');

    const pc = createPeer();
    const stream = await getLocalMedia(incomingTypeRef.current).catch(() => null);
    if (stream) stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    for (const c of iceCandidateQueue.current) {
      await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
    }
    iceCandidateQueue.current = [];
  }, [socketRef, createPeer, getLocalMedia]);

  const rejectCall = useCallback(() => {
    socketRef.current?.emit('call:reject');
    setCallStatus('idle');
  }, [socketRef]);

  // Reset when chat disconnects
  useEffect(() => {
    if (chatStatus !== 'connected' && callStatus !== 'idle') {
      cleanup();
    }
  }, [chatStatus, callStatus, cleanup]);

  // Socket event listeners — re-attach when socket becomes available
  useEffect(() => {
    const sock = socketRef.current;
    if (!sock) return;

    const handleIncoming = ({ type }: { type: CallType }) => {
      setCallStatus((prev) => {
        if (prev !== 'idle') {
          sock.emit('call:reject');
          return prev;
        }
        incomingTypeRef.current = type;
        setIncomingType(type);
        return 'receiving';
      });
    };

    const handleAccepted = async () => {
      setCallStatus('active');
      const pc = createPeer();
      const stream = await getLocalMedia(callTypeRef.current).catch(() => null);
      if (stream) stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sock.emit('call:signal', { type: 'offer', sdp: offer });
    };

    const handleRejected = () => setCallStatus('idle');

    const handleEnded = () => cleanup();

    const handleSignal = async (data: {
      type: string;
      sdp?: RTCSessionDescriptionInit;
      candidate?: RTCIceCandidateInit;
    }) => {
      const pc = peerRef.current;
      if (!pc) return;

      if (data.type === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp!));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sock.emit('call:signal', { type: 'answer', sdp: answer });
      } else if (data.type === 'answer') {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp!));
      } else if (data.type === 'candidate') {
        if (pc.remoteDescription) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate!)).catch(() => {});
        } else {
          iceCandidateQueue.current.push(data.candidate!);
        }
      }
    };

    sock.on('call:incoming', handleIncoming);
    sock.on('call:accepted', handleAccepted);
    sock.on('call:rejected', handleRejected);
    sock.on('call:ended', handleEnded);
    sock.on('call:signal', handleSignal);

    return () => {
      sock.off('call:incoming', handleIncoming);
      sock.off('call:accepted', handleAccepted);
      sock.off('call:rejected', handleRejected);
      sock.off('call:ended', handleEnded);
      sock.off('call:signal', handleSignal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketRef.current]);

  return {
    callStatus,
    callType,
    incomingType,
    localVideoRef,
    remoteVideoRef,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
  };
}
